import type { PlaybackSource } from "../utils/playbackSourcePresentation";

function normalizedIdentityPart(value: unknown) {
  return typeof value === "string"
    ? value.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase()
    : "";
}

/**
 * Remux source IDs can rotate when a catalog row is refreshed. Prefer the
 * concrete release identity so a refreshed row replaces its stale copy rather
 * than appearing beside it in the player menu.
 */
export function getPlaybackSourceCatalogIdentity(source: PlaybackSource) {
  const providerInfo = source.Remux?.ProviderInfo;
  const filename = normalizedIdentityPart(providerInfo?.filename);
  if (filename) {
    const addon = normalizedIdentityPart(providerInfo?.addon_id);
    const provider = normalizedIdentityPart(providerInfo?.source);
    return `release:${addon || provider || "remux"}:${filename}`;
  }
  return source.Id ? `id:${source.Id}` : "";
}

/**
 * Merge progressively richer source responses without allowing an incomplete
 * probe to erase streams already known for the same source.
 */
export function mergePlaybackSources(
  ...groups: Array<readonly PlaybackSource[] | undefined>
): PlaybackSource[] {
  const merged: PlaybackSource[] = [];
  for (const group of groups) {
    for (const entry of group ?? []) {
      const identity = getPlaybackSourceCatalogIdentity(entry);
      const index = merged.findIndex((candidate) =>
        Boolean(entry.Id && candidate.Id === entry.Id) ||
        Boolean(identity && getPlaybackSourceCatalogIdentity(candidate) === identity),
      );
      if (index < 0) {
        merged.push(entry);
        continue;
      }
      const existing = merged[index];
      if (existing.Id !== entry.Id) {
        // A new server identity is authoritative. Carrying old probe fields
        // across that boundary is how unrelated ASS metadata survived a Remux
        // release refresh in the first place.
        merged[index] = entry;
        continue;
      }
      merged[index] = {
        ...existing,
        ...entry,
        MediaStreams: entry.MediaStreams?.length
          ? entry.MediaStreams
          : existing.MediaStreams,
      };
    }
  }
  return merged;
}
